// UPLOAD-PREVIEW-2: one lightbox for a local File or an uploaded row.
// Local files are read in the browser. Office files wait until after upload.
import React, { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import api, { BACKEND_URL, getToken } from "@/lib/api";
import {
  displayName, fileType, formatBytes, isLocalFile, loadUploadBytes, matchLocalFile,
  previewKind, readLocalText,
} from "@/lib/filePreview";
import { pdfPageCount, renderPage } from "@/lib/pdfPreview";

const PDF_CAP = 30;

function authToken() {
  return typeof getToken === "function" ? getToken() : "";
}

function describe(entry, localFiles) {
  const local = !entry ? null : (isLocalFile(entry) ? entry : matchLocalFile(entry, localFiles));
  const row = entry && !isLocalFile(entry) ? entry : null;
  const src = local || row || {};
  return {
    local, row, src,
    kind: previewKind(src),
    name: displayName(src),
    size: src.size || 0,
    type: fileType(src) || "file",
  };
}

function PdfPages({ source, onTotal }) {
  const host = useRef(null);
  useEffect(() => {
    const el = host.current;
    if (!el || !source) return undefined;
    let dead = false;
    let drawn = 0;
    let total = 0;
    let counted = false;
    let busy = false;
    const width = () => el.clientWidth || 320;
    const sentinel = document.createElement("div");
    sentinel.setAttribute("data-testid", "pdf-sentinel");
    sentinel.style.height = "1px";
    el.appendChild(sentinel);
    const sentinelVisible = () => {
      const top = sentinel.offsetTop || 0;
      return top <= (el.scrollTop || 0) + (el.clientHeight || 0) + 48;
    };
    const more = async () => {
      if (dead || busy || drawn >= PDF_CAP) return;
      if (counted && total > 0 && drawn >= total) return;
      if (counted && total === 0 && drawn >= 1) return;
      busy = true;
      try {
        if (!counted) {
          total = Number(await pdfPageCount(source)) || 0;
          counted = true;
          if (!dead && onTotal) onTotal(total);
        }
        if (total > 0 && drawn >= total) return;
        if (total === 0 && drawn >= 1) return;
        const next = drawn + 1;
        if (next > PDF_CAP) return;
        const canvas = await renderPage(source, next, width());
        if (dead || !canvas) return;
        canvas.setAttribute("data-testid", `pdf-page-${next}`);
        canvas.className = "mb-2 w-full h-auto";
        el.insertBefore(canvas, sentinel);
        drawn = next;
      } catch {
        drawn = PDF_CAP;
      } finally {
        busy = false;
      }
      if (!dead && sentinelVisible()) more();
    };
    more();
    const onScroll = () => {
      if (sentinelVisible()) more();
    };
    el.addEventListener("scroll", onScroll);
    return () => {
      dead = true;
      el.replaceChildren();
      el.removeEventListener("scroll", onScroll);
    };
  }, [source, onTotal]);
  return <div ref={host} data-testid="pdf-preview" className="max-h-[60vh] overflow-auto" />;
}

export default function FileLightbox({
  items, index, onClose, localFiles, projectId, sessionId, container,
}) {
  const list = items || [];
  const files = localFiles || [];
  const [current, setCurrent] = useState(index || 0);
  const [text, setText] = useState("");
  const [textFailed, setTextFailed] = useState(false);
  const [pdfSource, setPdfSource] = useState(null);
  const [pdfTotal, setPdfTotal] = useState(0);
  const held = useRef(new Map());
  const touchX = useRef(0);
  const view = describe(list[current], files);

  useEffect(() => { setCurrent(index || 0); }, [index]);

  const go = (delta) => {
    setCurrent((value) => {
      const next = value + delta;
      if (next < 0 || next >= list.length) return value;
      return next;
    });
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "ArrowRight") go(1);
      else if (event.key === "ArrowLeft") go(-1);
      else if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const keep = new Set();
  const urls = {};
  [current - 1, current, current + 1].forEach((pos) => {
    const entry = describe(list[pos], files);
    if (entry.local && entry.kind === "image") {
      const key = `${pos}:${entry.name}:${entry.size}`;
      keep.add(key);
      if (!held.current.has(key)) held.current.set(key, URL.createObjectURL(entry.local));
      urls[pos] = held.current.get(key);
    }
  });
  useEffect(() => {
    held.current.forEach((url, key) => {
      if (!keep.has(key)) {
        URL.revokeObjectURL(url);
        held.current.delete(key);
      }
    });
  });
  useEffect(() => () => {
    held.current.forEach((url) => URL.revokeObjectURL(url));
    held.current.clear();
  }, []);

  useEffect(() => {
    let dead = false;
    setText("");
    setTextFailed(false);
    if (view.local && view.kind === "text") {
      readLocalText(view.local).then((value) => { if (!dead) setText(value); }).catch(() => {});
    } else if (view.row && (view.kind === "text" || view.kind === "office") && projectId && sessionId) {
      api.get(`/projects/${projectId}/bulk-upload/${sessionId}/files/${view.row.file_id}/preview-text`)
        .then((res) => { if (!dead) setText((res.data && res.data.text) || ""); })
        .catch(() => { if (!dead) setTextFailed(true); });
    }
    return () => { dead = true; };
  }, [current, view.kind, view.local, view.row, projectId, sessionId]);

  useEffect(() => {
    let dead = false;
    setPdfSource(null);
    setPdfTotal(0);
    if (view.kind !== "pdf") return undefined;
    if (view.local) {
      setPdfSource(view.local);
      return () => { dead = true; };
    }
    const url = view.row && (view.row.url || view.row.preview_url);
    if (!url) return undefined;
    loadUploadBytes(`${BACKEND_URL || ""}${url}`, authToken())
      .then((buf) => { if (!dead) setPdfSource(buf); })
      .catch(() => {});
    return () => { dead = true; };
  }, [current, view.kind, view.local, view.row]);

  const serverImage = view.row ? `${BACKEND_URL || ""}${view.row.preview_url || view.row.url || ""}` : "";
  const imageSrc = view.kind === "image" ? (urls[current] || serverImage) : "";
  const before = !view.row;
  const unavailable = before && (view.kind === "office" || view.kind === "other");
  const download = !before && view.kind === "other";
  const showText = view.kind === "text" || (view.kind === "office" && view.row);

  const body = (
    <>
      <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/50" />
      <DialogPrimitive.Content
        data-testid="upload-preview"
        className="fixed inset-0 z-[71] flex items-center justify-center p-3"
        onTouchStart={(event) => { touchX.current = event.changedTouches[0].clientX; }}
        onTouchEnd={(event) => {
          const dx = event.changedTouches[0].clientX - touchX.current;
          if (dx <= -48) go(1);
          else if (dx >= 48) go(-1);
        }}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
            <button type="button" data-testid="lightbox-prev" aria-label="Previous file" onClick={() => go(-1)} className="px-2 text-sm text-slate-600">Prev</button>
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-sm font-semibold text-slate-800">{view.name}</DialogPrimitive.Title>
              <DialogPrimitive.Description className="truncate text-xs text-slate-500">
                {`${formatBytes(view.size)} ${view.type}`}
              </DialogPrimitive.Description>
            </div>
            <p data-testid="lightbox-count" className="shrink-0 text-xs text-slate-500">{`${current + 1} of ${list.length}`}</p>
            <button type="button" data-testid="lightbox-next" aria-label="Next file" onClick={() => go(1)} className="px-2 text-sm text-slate-600">Next</button>
            <button type="button" onClick={onClose} aria-label="Close preview" className="p-1 text-slate-400"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 overflow-auto p-3">
            {view.kind === "image" && imageSrc ? (
              <img src={imageSrc} alt="" data-testid="image-preview" className="mx-auto max-h-[60vh] max-w-full" />
            ) : null}
            {view.kind === "pdf" ? (
              <div className="space-y-2">
                {pdfSource ? <PdfPages source={pdfSource} onTotal={setPdfTotal} /> : <div data-testid="pdf-preview" />}
                {view.row && view.row.url ? (
                  <a href={`${BACKEND_URL || ""}${view.row.url}`} target="_blank" rel="noopener noreferrer" data-testid="pdf-new-tab" className="text-sm text-blue-600">Open in a new tab</a>
                ) : null}
                {pdfTotal > PDF_CAP ? <p className="text-xs text-slate-500">Showing the first 30 pages.</p> : null}
              </div>
            ) : null}
            {showText ? (
              textFailed
                ? <p data-testid="preview-text-error" className="text-sm text-slate-500">Preview text is unavailable.</p>
                : <pre data-testid="preview-text" className="whitespace-pre-wrap text-sm text-slate-700">{text}</pre>
            ) : null}
            {unavailable ? (
              <div data-testid="preview-unavailable" className="space-y-1">
                <p className="text-sm text-slate-800">No preview for this file type before upload</p>
                <p className="text-sm text-slate-700">{view.name}</p>
                <p className="text-xs text-slate-500">{`${formatBytes(view.size)} ${view.type}`}</p>
              </div>
            ) : null}
            {download ? (
              <div data-testid="preview-download-panel" className="space-y-2">
                <p className="text-sm text-slate-800">{view.name}</p>
                <p className="text-xs text-slate-500">{formatBytes(view.size)}</p>
                <a href={`${BACKEND_URL || ""}${view.row.url || ""}`} data-testid="preview-download" className="text-sm text-blue-600">Download</a>
              </div>
            ) : null}
          </div>
        </div>
      </DialogPrimitive.Content>
    </>
  );

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      {container ? <DialogPrimitive.Portal container={container}>{body}</DialogPrimitive.Portal> : body}
    </DialogPrimitive.Root>
  );
}
